/**
 * A collectable coin that spawns in the maze, has a subtle pulsing glow,
 * and self-destructs after a limited time.
 *
 * Collecting 10 coins awards the player an extra life.
 */

import Phaser from 'phaser';
import { TEX_COIN } from '../utils/constants.js';
import {
  COIN_FADE_DURATION,
  SCORE_PER_COIN,
  TILE_SIZE,
  AUDIO_COIN,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

export default class CoinItem extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x          - World x position (center of tile).
   * @param {number} y          - World y position (center of tile).
   * @param {number} lifetime   - Milliseconds before the coin fades and disappears.
   */
  constructor(scene, x, y, lifetime) {
    super(scene, x, y, TEX_COIN);

    this.scene = scene;
    this.scoreValue = SCORE_PER_COIN;

    // Add to scene
    scene.add.existing(this);

    // Render above hazard floor tiles (water/lava/poison are at depth 2)
    this.setDepth(5);

    // Pop-in effect
    this.setScale(0);
    scene.tweens.add({
      targets: this,
      scaleX: 1,
      scaleY: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });

    // Subtle glow effect: pulsing alpha
    this._createGlowEffect();

    // Schedule disappearance
    this._lifetimeTimer = scene.time.delayedCall(lifetime, () => {
      this._fadeOut();
    });
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Called when the player picks up this coin.
   * Plays the coin sound, a collect animation, then destroys itself.
   */
  collect() {
    // Immediately deactivate to prevent physics overlap firing again
    this.active = false;

    // Cancel the lifetime timer
    if (this._lifetimeTimer) {
      this._lifetimeTimer.destroy();
      this._lifetimeTimer = null;
    }

    // Guard: already destroyed
    if (!this.scene) return;

    // Play coin sound
    this.scene.sound.play(AUDIO_COIN, { volume: getSfxVol(0.5) });

    // Show "+1 Coin" label at the collection spot
    this._showCollectLabel();

    // Destroy glow
    this._destroyGlow();

    // Quick flash + scale up effect
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.8,
      scaleY: 1.8,
      alpha: 0,
      duration: 200,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.destroy();
      },
    });
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Show "+1 Coin" at the collection spot with a zoom-in + float-up +
   * fade-out effect, matching the food collection label style but with
   * a subtle golden stroke.
   * @private
   */
  _showCollectLabel() {
    if (!this.scene) return;

    // Capture scene reference — `this.scene` will be null after the
    // CoinItem is destroyed, but the label lives independently.
    const scene = this.scene;

    const label = scene.add.text(
      this.x,
      this.y,
      '+1 Coin',
      {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#ffffff',
        stroke: '#b8960c',
        strokeThickness: 2,
        fontStyle: 'bold',
      },
    );
    label.setOrigin(0.5, 0.5);
    label.setDepth(150);
    label.setScale(0.5);
    label.setAlpha(1);

    // Zoom in + hold + float up + fade out
    // First: quick zoom-in to full size
    scene.tweens.add({
      targets: label,
      scaleX: 2.2,
      scaleY: 2.2,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Hold visible briefly, then float up and fade
        if (!label.active) return;
        scene.tweens.add({
          targets: label,
          y: label.y - TILE_SIZE * 1.2,
          alpha: 0,
          duration: 1200,
          delay: 250,
          ease: 'Quad.easeIn',
          onComplete: () => {
            label.destroy();
          },
        });
      },
    });
  }

  /**
   * Create a subtle pulsing glow ring around the coin using a Graphics
   * object that oscillates in alpha via a tween.
   * @private
   */
  _createGlowEffect() {
    // Draw a glow ring as a Graphics circle attached to the scene
    this._glow = this.scene.add.graphics();
    this._glow.setDepth(this.depth - 1);
    this._updateGlowPosition();

    // Draw the glow ring
    this._glow.clear();
    this._glow.fillStyle(0xffffff, 0.12);
    this._glow.fillCircle(0, 0, 10);
    this._glow.fillStyle(0xffffff, 0.06);
    this._glow.fillCircle(0, 0, 14);

    // Pulse the glow alpha
    this._glowTween = this.scene.tweens.add({
      targets: this._glow,
      alpha: { from: 1, to: 0.3 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Also do a gentle float for the coin sprite itself
    this._floatTween = this.scene.tweens.add({
      targets: this,
      y: this.y - 2,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this._updateGlowPosition();
      },
    });
  }

  /**
   * Update the glow position to match the coin sprite.
   * @private
   */
  _updateGlowPosition() {
    if (this._glow) {
      this._glow.setPosition(this.x, this.y);
    }
  }

  /**
   * Destroy the glow graphics and its tween.
   * @private
   */
  _destroyGlow() {
    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween = null;
    }
    if (this._glow) {
      this._glow.destroy();
      this._glow = null;
    }
  }

  /**
   * Fade out and blink before disappearing.
   * @private
   */
  _fadeOut() {
    if (!this.scene) return;

    const blinkDuration = COIN_FADE_DURATION * 0.6;
    const fadeDuration = COIN_FADE_DURATION * 0.4;

    // Stop float
    if (this._floatTween) {
      this._floatTween.stop();
    }

    // Blink phase
    this.scene.tweens.add({
      targets: this,
      alpha: 0.3,
      duration: 100,
      yoyo: true,
      repeat: Math.floor(blinkDuration / 200),
      onComplete: () => {
        if (!this.scene) return;

        // Final fade + shrink
        this.scene.tweens.add({
          targets: this,
          alpha: 0,
          scaleX: 0.3,
          scaleY: 0.3,
          duration: fadeDuration,
          ease: 'Quad.easeIn',
          onComplete: () => {
            this.destroy();
          },
        });
      },
    });

    // Also fade the glow
    if (this._glow) {
      if (this._glowTween) {
        this._glowTween.stop();
      }
      this.scene.tweens.add({
        targets: this._glow,
        alpha: 0,
        duration: blinkDuration + fadeDuration,
        ease: 'Quad.easeIn',
      });
    }
  }

  /**
   * Clean up timers, tweens, and glow before destruction.
   * @param {boolean} [fromScene]
   */
  destroy(fromScene) {
    if (this._lifetimeTimer) {
      this._lifetimeTimer.destroy();
      this._lifetimeTimer = null;
    }
    if (this._floatTween) {
      this._floatTween.stop();
      this._floatTween = null;
    }
    this._destroyGlow();
    super.destroy(fromScene);
  }
}
